import { JSX, useMemo } from "react";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { Alert, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useWeatherForecastQuery } from "src/api/locationChartQueries.api";
import { getTomorrow } from "src/lib/date.helper";
import { type LocationModel } from "src/types/location";

import WeatherForecast from "./WeatherForecast";
import { getStyles } from "../info.styles";

interface ExpectedWeatherInfoProps {
  selectedLocation: LocationModel;
  isWeatherAvailable?: boolean;
}

function ExpectedWeatherInfo({ selectedLocation, isWeatherAvailable = true }: ExpectedWeatherInfoProps): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const tomorrow = useMemo(() => getTomorrow(), []); // tomorrow
  const { data } = useWeatherForecastQuery(selectedLocation.GeoCoordinateId, tomorrow);

  const forecast = data?.value?.[0];
  const weatherForecastAvailable = !!(
    typeof forecast?.ApparentTempHigh === "number" &&
    typeof forecast?.ApparentTempLow === "number" &&
    !!forecast?.Status &&
    isWeatherAvailable
  );

  return (
    <Alert
      severity={weatherForecastAvailable ? "success" : "error"}
      icon={<AutoFixHighIcon fontSize='small' />}
      action={weatherForecastAvailable ? <WeatherForecast forecast={forecast} /> : null}
      sx={getStyles(theme).alert}
    >
      <Typography variant='body2'>
        {weatherForecastAvailable
          ? `${t("Expected weather tomorrow")}, ${tomorrow.format("D ")}${t(tomorrow.format("MMM"))}`
          : t("Weather data for tomorrow is currently unavailable")}
      </Typography>
    </Alert>
  );
}

export default ExpectedWeatherInfo;
